import { z } from "zod";

// ---------------------------------------------------------------------------
// Daily tasks ("Dagens uppgifter")
//
// The store manager sends an ordered task list to a department head for a
// day; the head starts, finishes and rates each task; the manager follows up.
// Stored at:
//   Businesses/{businessId}/dailyTasks/{taskId}
// ---------------------------------------------------------------------------

export const DailyTaskStatus = z.enum(["new", "started", "done"]);
export type DailyTaskStatus = z.infer<typeof DailyTaskStatus>;

export const DailyTaskRating = z.enum(["bra", "okej", "tungt"]);
export type DailyTaskRating = z.infer<typeof DailyTaskRating>;

export const DailyTaskDeadline = z.enum(["today", "tomorrow", "week"]);
export type DailyTaskDeadline = z.infer<typeof DailyTaskDeadline>;

export interface DailyTaskRecord {
  id: string;
  businessId: string;
  /** The department head (roster member id) the task was sent to. */
  headMemberId: string;
  salesTeamId: string | null;
  /** YYYY-MM-DD the task belongs to. */
  date: string;
  order: number;
  title: string;
  requiresPhoto: boolean;
  deadline: DailyTaskDeadline;
  status: DailyTaskStatus;
  startedAt: string | null;
  doneAt: string | null;
  minutes: number | null;
  rating: DailyTaskRating | null;
  comment: string | null;
  photoPath: string | null;
  /** "anser" when Anser suggested it, otherwise null. */
  source: string | null;
  sentByUid: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export const ListDailyTasksInput = z.object({
  businessId: z.string().min(1),
  /** YYYY-MM-DD, inclusive. */
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  headMemberId: z.string().min(1).optional(),
});
export type ListDailyTasksInput = z.infer<typeof ListDailyTasksInput>;

export const SendDailyTasksInput = z.object({
  businessId: z.string().min(1),
  headMemberIds: z.array(z.string().min(1)).min(1),
  /** YYYY-MM-DD dates the same list applies to. */
  dates: z.array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)).min(1),
  tasks: z
    .array(
      z.object({
        title: z.string().min(1).max(160),
        requiresPhoto: z.boolean().default(false),
        deadline: DailyTaskDeadline.default("today"),
        source: z.string().min(1).nullable().optional(),
      }),
    )
    .min(1),
});
export type SendDailyTasksInput = z.infer<typeof SendDailyTasksInput>;

export const SetDailyTaskStatusInput = z.object({
  businessId: z.string().min(1),
  taskId: z.string().min(1),
  status: DailyTaskStatus,
});
export type SetDailyTaskStatusInput = z.infer<typeof SetDailyTaskStatusInput>;

export const RateDailyTaskInput = z.object({
  businessId: z.string().min(1),
  taskId: z.string().min(1),
  rating: DailyTaskRating,
  minutes: z.number().int().min(0).max(600).nullable().optional(),
  comment: z.string().max(500).nullable().optional(),
});
export type RateDailyTaskInput = z.infer<typeof RateDailyTaskInput>;
